import { useState } from 'react';
import { aiSermonSummary } from '../services/groqAi';
import { formatSermonSummary, buildHandoutHtml } from '../lib/sermonSummary';
import { toast } from '../lib/toast';

const todayLabel = () => new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

/**
 * AI Tools → Sermon Summary: summarize what the voice detection has heard, then
 * download it as text, copy it, or open a printable handout (save as PDF).
 * `title` and `speaker` are optional and appear on the output.
 */
export default function useSermonSummary({ transcribedText, addDebugLog, logActivity }) {
  const [sermonSummary, setSermonSummary] = useState(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [sermonTitle, setSermonTitle] = useState('');
  const [speaker, setSpeaker] = useState('');

  const details = () => ({ title: sermonTitle.trim(), speaker: speaker.trim() });

  const downloadSummary = () => {
    if (!sermonSummary) return;

    const date = todayLabel();
    const blob = new Blob([formatSermonSummary(sermonSummary, date, details())], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sermon-summary-${date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copySummary = async () => {
    if (!sermonSummary) return false;
    try {
      await navigator.clipboard.writeText(formatSermonSummary(sermonSummary, todayLabel(), details()));
      addDebugLog('📋 Sermon summary copied');
      toast.success('Summary copied to the clipboard');
      return true;
    } catch {
      toast.error('Could not copy. Use Download instead.');
      return false;
    }
  };

  // Opens the handout in its own window and prints it; "Save as PDF" is in the print dialog.
  const printHandout = () => {
    if (!sermonSummary) return;
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('The handout window was blocked. Allow pop-ups for this site and try again.');
      return;
    }
    win.document.open();
    win.document.write(buildHandoutHtml(sermonSummary, todayLabel(), details()));
    win.document.close();
    win.focus();
    // Give the new window a moment to lay out before the print dialog opens.
    setTimeout(() => { try { win.print(); } catch { /* the user can print from the window */ } }, 300);
    logActivity('sermon_handout_printed', {});
  };

  const summarizeSermon = async () => {
    if (!transcribedText || transcribedText.trim().length < 50) {
      toast.info('Need more sermon transcript to generate a summary. Keep listening for a while first.');
      return;
    }

    setIsSummarizing(true);
    addDebugLog('🤖 Generating sermon summary with SleekHear AI...');

    try {
      const summary = await aiSermonSummary(transcribedText);
      setSermonSummary(summary);
      addDebugLog(`✅ Sermon summary generated`);
      logActivity('sermon_summarized', { transcriptLength: transcribedText.length });
    } catch (error) {
      addDebugLog(`❌ Sermon summary failed: ${error.message}`);
      toast.error(`Failed to generate summary: ${error.message}`);
    } finally {
      setIsSummarizing(false);
    }
  };

  return {
    sermonSummary, isSummarizing, summarizeSermon,
    downloadSummary, copySummary, printHandout,
    sermonTitle, setSermonTitle, speaker, setSpeaker,
  };
}
